/**
 * The four locales (BP-19 §3, ARCHITECTURE §3 i18n).
 *
 * The list is closed and matches `Locale` in `api/app/modules/settings/registry.py`
 * exactly: the server validates a written locale against that `Literal`, so a fifth
 * language added here and not there would be accepted by the switcher and refused by
 * `PUT /me/locale`.
 *
 * Nothing in here touches i18next. The chain of rule 1 — client/user locale → tenant
 * default → `en` — is a pure function of three strings, which is what makes it
 * testable without a running instance and reusable by the client shell, which has no
 * tenant default to read (rule 6).
 */

/** The four, in the order the switcher lists them. */
export const LOCALES = ['en', 'hi', 'mr', 'gu'] as const;

export type LocaleCode = (typeof LOCALES)[number];

/** The fallback of last resort, and the only locale every namespace is complete in. */
export const FALLBACK_LOCALE: LocaleCode = 'en';

/**
 * Each language's name *in that language*. A switcher that writes "Hindi" in English
 * is no use to the person who needs it; "हिन्दी" is legible to exactly the reader who
 * is looking for it.
 */
export const LOCALE_NAMES: Record<LocaleCode, string> = {
  en: 'English',
  hi: 'हिन्दी',
  mr: 'मराठी',
  gu: 'ગુજરાતી',
};

/**
 * The BCP-47 tag each locale formats *dates* with (BP-19 §2, rule 3).
 *
 * `en` keeps `en-GB` — the tag BP-04 and BP-06 already format with, so an English
 * screen renders exactly what it rendered before BP-19 — and the other three take
 * their Indian tag, so a Hindi booking card reads "सोम, 5 अक्तू" rather than
 * "Mon, 5 Oct". **Money is not in here on purpose**: rule 3 keeps every amount at
 * `en-IN` whatever the page's language, because ₹1,23,456.00 is how a bill reads in
 * Mumbai in any of the four, and a client checking a total against the paper should
 * not have to re-group it in their head.
 */
export const LOCALE_DATE_TAGS: Record<LocaleCode, string> = {
  en: 'en-GB',
  // `-u-nu-latn` on all three, and it is not decoration: `mr-IN` defaults to
  // Devanagari digits, so a Marathi booking card would read "सोम, ५ ऑक्टो" while the
  // price beside it read "1,23,456.00". Indian apps write the numerals in Latin;
  // mixing the two inside one card is worse than either.
  hi: 'hi-IN-u-nu-latn',
  mr: 'mr-IN-u-nu-latn',
  gu: 'gu-IN-u-nu-latn',
};

/** True when `value` is one of the four. Narrow before storing or sending. */
export function isLocale(value: unknown): value is LocaleCode {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Rule 1's chain, as a fold: the first candidate that is one of the four, else `en`.
 *
 * Callers pass the chain in priority order — `resolveLocale(user.locale, tenantDefault)`
 * in the staff shell, `resolveLocale(stored, deviceLocale())` in the client shell. A
 * `null`, an `undefined` or a language this build does not carry is simply skipped,
 * so a tenant whose default was set before a language shipped still renders.
 */
export function resolveLocale(...candidates: readonly (string | null | undefined)[]): LocaleCode {
  for (const candidate of candidates) {
    if (isLocale(candidate)) {
      return candidate;
    }
  }
  return FALLBACK_LOCALE;
}

/** What `navigator` has to say. Narrowed so a test can hand it a plain object. */
export type LanguageSource = {
  readonly languages?: readonly string[] | undefined;
  readonly language?: string | undefined;
};

/**
 * The device's language, when it is one of the four (BP-19 rule 6, §14 default).
 *
 * `navigator.languages` is the ordered preference list and `navigator.language` the
 * single top choice; a browser may expose either. Tags are matched on their primary
 * subtag, so `hi-IN`, `hi` and `HI-Latn` all answer `hi` — the region never changes
 * which bundle is right, and Devanagari-vs-Latin Hindi is not a distinction this
 * build makes.
 *
 * Returns `null` rather than `en` when nothing matches, so the caller can tell "the
 * device asked for English" apart from "the device asked for Tamil and we have none".
 */
export function deviceLocale(source: LanguageSource | undefined): LocaleCode | null {
  if (source === undefined) {
    return null;
  }
  const tags = source.languages ?? (source.language === undefined ? [] : [source.language]);
  for (const tag of tags) {
    const primary = tag.split('-')[0]?.toLowerCase();
    if (isLocale(primary)) {
      return primary;
    }
  }
  return null;
}
